import { Box, Button, Card, CardContent, CardHeader, TextField, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { CustomerDto } from '../../../interactors/gen/backendClient'
import { Save } from '@mui/icons-material'

interface Props {
  association: CustomerDto
  onSubmit: (data: Partial<CustomerDto>) => Promise<void>
}

export const BannerColorCard: React.FC<Props> = ({ association, onSubmit }) => {
  const [bannerColor, setBannerColor] = useState(association?.pageBannerColor || '#F3B700')
  const [hexInput, setHexInput] = useState(association?.pageBannerColor || '#F3B700')
  const [hexError, setHexError] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setBannerColor(association?.pageBannerColor || '#F3B700')
    setHexInput(association?.pageBannerColor || '#F3B700')
    setHasChanges(false)
  }, [association?.pageBannerColor])

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value
    setBannerColor(newColor)
    setHexInput(newColor)
    setHasChanges(newColor !== association?.pageBannerColor)
    setHexError('')
  }

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setHexInput(value)

    const hexRegex = /^#[0-9A-Fa-f]{6}$/
    if (hexRegex.test(value)) {
      setHexError('')
      setBannerColor(value)
      setHasChanges(value !== association?.pageBannerColor)
    } else {
      setHexError('Le code couleur doit être au format HEX (ex: #F3B700)')
    }
  }

  const handleSave = async () => {
    if (hexError) return

    setIsSaving(true)
    try {
      await onSubmit({ pageBannerColor: bannerColor })
      setHasChanges(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setBannerColor(association?.pageBannerColor || '#F3B700')
    setHexInput(association?.pageBannerColor || '#F3B700')
    setHasChanges(false)
    setHexError('')
  }

  return (
    <Card>
      <CardHeader title="Couleur de la bannière" />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body2" color="textSecondary">
            Personnalisez la couleur de la bannière sur votre page Petso
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Sélecteur de couleur
              </Typography>
              <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
                <TextField
                  type="color"
                  value={bannerColor}
                  onChange={handleColorChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    style: { height: 56, width: 120, cursor: 'pointer', padding: 0 },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 56,
                      padding: 0,
                    },
                    '& .MuiInputBase-input': {
                      padding: 0,
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Code hexadécimal
              </Typography>
              <TextField
                type="text"
                value={hexInput}
                onChange={handleHexInputChange}
                error={!!hexError}
                helperText={hexError}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 150,
                  '& .MuiInputBase-root': {
                    height: 56,
                  },
                }}
                placeholder="#F3B700"
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Aperçu
              </Typography>
              <Box
                sx={{
                  width: 150,
                  height: 56,
                  backgroundColor: bannerColor,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    textShadow: '0 0 4px rgba(0,0,0,0.5)',
                  }}
                >
                  Votre bannière
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={!hasChanges || !!hexError || isSaving}
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            {hasChanges && (
              <Button variant="outlined" onClick={handleReset} disabled={isSaving}>
                Annuler
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
